import React from 'react';
import { motion } from 'framer-motion';
import { StoicQuote } from '@/constants/stoicQuotes';
import styles from '../ShutdownRitual.module.css';

interface QuoteStepProps {
  quote: StoicQuote;
  summaryData: {
    blocksReviewed: number;
    blocksCompleted: number;
    unfinishedHandled: number;
    tasksCaptured: number;
    tomorrowBlocksPlanned: number;
    hasReflection: boolean;
  };
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 300, damping: 25 },
  },
};

export function QuoteStep({ quote, summaryData }: QuoteStepProps) {
  const summaryItems = [
    { label: 'Blocks reviewed', value: summaryData.blocksReviewed, show: true },
    {
      label: 'Blocks completed',
      value: summaryData.blocksCompleted,
      show: summaryData.blocksCompleted > 0,
    },
    {
      label: 'Unfinished handled',
      value: summaryData.unfinishedHandled,
      show: summaryData.unfinishedHandled > 0,
    },
    {
      label: 'Tasks captured',
      value: summaryData.tasksCaptured,
      show: summaryData.tasksCaptured > 0,
    },
    {
      label: 'Tomorrow planned',
      value: summaryData.tomorrowBlocksPlanned,
      show: summaryData.tomorrowBlocksPlanned > 0,
    },
    { label: 'Reflection written', value: summaryData.hasReflection ? 'Yes' : 'No', show: true },
  ].filter((item) => item.show);

  return (
    <motion.div
      className={styles.stepContent}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.h2 className={styles.stepTitle} variants={itemVariants}>
        Daily Stoic Wisdom
      </motion.h2>
      <motion.p className={styles.stepSubtitle} variants={itemVariants}>
        Ancient wisdom to guide your reflection and close out the day.
      </motion.p>

      {/* Quote */}
      <motion.div
        className={styles.quoteContainer}
        variants={itemVariants}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: 'spring', stiffness: 200, damping: 20 }}
      >
        <motion.p
          className={styles.quoteText}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          "{quote.text}"
        </motion.p>
        <motion.p
          className={styles.quoteAuthor}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          — {quote.author}
          {quote.source && (
            <span style={{ display: 'block', fontSize: '12px', opacity: 0.7, marginTop: '4px' }}>
              {quote.source}
            </span>
          )}
        </motion.p>
      </motion.div>

      {/* Summary */}
      <motion.div variants={itemVariants}>
        <h3
          style={{
            fontSize: '14px',
            color: '#888',
            marginBottom: '16px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            textAlign: 'center',
          }}
        >
          Your Shutdown Summary
        </h3>
        <div className={styles.summaryList}>
          {summaryItems.map((item, index) => (
            <motion.div
              key={item.label}
              className={styles.summaryItem}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
            >
              <span className={styles.summaryIcon} aria-hidden="true">
                {typeof item.value === 'number'
                  ? item.value > 0
                    ? '+'
                    : '-'
                  : item.value === 'Yes'
                    ? '+'
                    : '-'}
              </span>
              <span className={styles.summaryText}>
                {item.label}: <strong>{item.value}</strong>
              </span>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Ready message */}
      <motion.p
        style={{ fontSize: '14px', color: '#68d391', textAlign: 'center', marginTop: '32px' }}
        variants={itemVariants}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
      >
        You are ready to shut down. Press Complete to finish.
      </motion.p>
    </motion.div>
  );
}

export default QuoteStep;
